import { closingStatusToNumber, isOpenCategory } from './constants';
import type {
  ClosingStatus,
  LeadPipelineResponse,
  LeadPipelineStageCategory,
  LeadPipelineStageDefinition,
  LeadSourceDefinition,
} from './types';

function asBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  return Boolean(value);
}

function normalizePipelineStageCategory(value: unknown): LeadPipelineStageCategory {
  if (typeof value === 'number' && (value === 1 || value === 2)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.toLowerCase() === 'open') {
      return 1;
    }
    if (trimmed.toLowerCase() === 'closed') {
      return 2;
    }
  }
  return 1;
}

export function normalizeLeadPipelineStage(
  stage: LeadPipelineStageDefinition & { IsActive?: unknown; Category?: unknown },
): LeadPipelineStageDefinition {
  const closingStatus =
    stage.closingStatus === null || stage.closingStatus === undefined
      ? null
      : (closingStatusToNumber(stage.closingStatus as ClosingStatus) as ClosingStatus);

  return {
    id: stage.id,
    code: stage.code,
    label: stage.label,
    sortOrder: stage.sortOrder,
    category: normalizePipelineStageCategory(stage.category ?? stage.Category),
    closingStatus: closingStatus === 0 ? null : closingStatus,
    isActive: asBoolean(stage.isActive ?? stage.IsActive),
  };
}

export function normalizeLeadPipelineResponse(
  raw: LeadPipelineResponse & { Stages?: LeadPipelineStageDefinition[] },
): LeadPipelineResponse {
  const stages = raw.stages ?? raw.Stages ?? [];
  return {
    version: raw.version,
    closeRules: raw.closeRules,
    stages: stages.map((stage) => normalizeLeadPipelineStage(stage)),
  };
}

export function normalizeLeadSource(
  source: LeadSourceDefinition & { IsActive?: unknown },
): LeadSourceDefinition {
  return {
    id: source.id,
    code: source.code,
    label: source.label,
    sortOrder: source.sortOrder,
    isActive: asBoolean(source.isActive ?? source.IsActive),
  };
}

export function activeLeadSources(sources: LeadSourceDefinition[] | undefined): LeadSourceDefinition[] {
  return (sources ?? []).filter((source) => source.isActive);
}

export function activeLeadPipelineStages(
  stages: LeadPipelineStageDefinition[] | undefined,
): LeadPipelineStageDefinition[] {
  return (stages ?? []).filter((stage) => stage.isActive);
}

export function openLeadPipelineStages(
  stages: LeadPipelineStageDefinition[] | undefined,
): LeadPipelineStageDefinition[] {
  return activeLeadPipelineStages(stages).filter((stage) => isOpenCategory(stage.category));
}
